import { CurrentUser } from '@appTypes/CurrentUser';
import { DealerState } from '@appTypes/DealerState';
import { InventoryResource } from '@appTypes/InvetoryResource';
import Loader from '@components/shared/loader/Loader';
import { Icon, Typography } from '@components/style';
import {
  checkDealTypes,
  submitChangesCalculation,
  updateVehicleData,
} from '@hooks/Inventory/UseBulkInventory';
import { ErrorToast } from '@services/utils/ToastUtils';
import { AppState } from '@store/AppState';
import { setIsInventoryBrowserModalOpen } from '@store/dealsheet/DealsheetActions';
import {
  bulkChangeReplacement,
  InventoryRemove,
  SetInventoryRemainingModal,
  SetInventoryRemainingModalData,
} from '@store/inventory/InventoryActions';
import { resetFilters } from '@store/inventory/InventoryFilterActions';
import { setPortfolioShouldRefresh } from '@store/portfolio/PortfolioActions';
import { theme } from '@theme';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';
import styled from 'styled-components';
import ReplaceChangeModal from '../modals/bulk-inventory-browser/ReplaceChangeModal';
import VehicleInventoryImage from './VehicleInventoryImage';

export type VehicleListProps = {
  vehicles: InventoryResource[];
  loading: boolean;
  selectVehicle: (vehicle: InventoryResource) => void;
  bulkData?: any;
  dealCount?: number;
};

const VehicleList: React.FC<VehicleListProps> = ({
  vehicles,
  loading,
  selectVehicle,
  bulkData,
  dealCount,
}) => {
  const { permissions } = useSelector<AppState, CurrentUser>((state) => state.currentUser);
  const { featureToggle, preownedStatus } = useSelector<AppState, DealerState>(
    (state) => state.dealer,
  );
  const [loadingPage, SetLoadingPage] = useState(false);
  const [isSubmitChangeModalOpen, setIsSubmitChangeModalOpen] = useState(false);
  const [submitChangeModalData, setSubmitChangeModalData] = useState<any>({});
  const pathParts = location.pathname.split('/');
  const urlType = pathParts[2];

  useEffect(() => {
    SetLoadingPage(loading);
  }, [loading]);

  const isEnabledBasedOnEligibiltyCheck = (vehicle, dealType) => {
    switch (dealType) {
      case 'cash':
        return vehicle.isAvailableForReplacementCash;
      case 'finance':
        return vehicle.isAvailableForReplacementFinance;
      case 'lease':
        return vehicle.isAvailableForReplacementLease;
      case 'all':
        return (
          (checkDealTypes(bulkData).hasCashDeals && vehicle.isAvailableForReplacementCash) ||
          (checkDealTypes(bulkData).hasFinanceDeals && vehicle.isAvailableForReplacementFinance) ||
          (checkDealTypes(bulkData).hasLeaseDeals && vehicle.isAvailableForReplacementLease)
        );
    }
  };
  const isLeaseEnabled = (vehicle) => {
    const vehicleType = vehicle.stockType.toUpperCase();

    switch (vehicleType) {
      case 'N/A':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
      case 'NEW':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
      case 'USED':
      case 'PRE-OWNED':
        return false;
      case 'SAMPLE':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'lease');
    }
  };

  const isFinanceEnabled = (vehicle) => {
    const vehicleType = vehicle.stockType.toUpperCase();

    switch (vehicleType) {
      case 'N/A':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
      case 'NEW':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
      case 'USED':
      case 'PRE-OWNED':
        return true;
      case 'SAMPLE':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'finance');
    }
  };

  const isCashEnabled = (vehicle) => {
    const vehicleType = vehicle.stockType.toUpperCase();

    switch (vehicleType) {
      case 'N/A':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
      case 'NEW':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
      case 'USED':
      case 'PRE-OWNED':
        return true;
      case 'SAMPLE':
        return isEnabledBasedOnEligibiltyCheck(vehicle, 'cash');
    }
  };

  const getVehicleTypeBadge = (vehicleType) => {
    if (!vehicleType) {
      return null;
    }
    switch (vehicleType.toLowerCase()) {
      case 'new':
        return 'new';
        break;
      case 'pre-owned':
        return '';
        break;
      case 'buyout':
        return 'buyout';
        break;
      case 'certified pre-owned':
        return '';
      default:
        return '';
    }
  };

  const dispatch = useDispatch();
  const onResetFiltersClick = () => {
    dispatch(resetFilters());
  };

  const getSize = (index) => {
    const vehicle = vehicles[index];
    const size = `${vehicle.year} ${vehicle.brandName} ${vehicle.trim}`.replace(/\s/g, '').length;
    return size > 38 ? 196 : 184;
  };

  const anyDealNotApplicableForReplacement = (vehicle) => {
    return (
      (!vehicle.isAvailableForReplacementCash && checkDealTypes(bulkData).hasCashDeals) ||
      (!vehicle.isAvailableForReplacementFinance && checkDealTypes(bulkData).hasFinanceDeals) ||
      (!vehicle.isAvailableForReplacementLease && checkDealTypes(bulkData).hasLeaseDeals)
    );
  };

  const checkWarningButton = (vehicle) => {
    if (checkDealTypes(bulkData).multipleSelected) {
      if (isLeaseDeal()) {
        return (
          vehicle.isUsed ||
          (!vehicle.isOffBrand &&
            vehicle.stockType.toUpperCase() === 'SAMPLE' &&
            anyDealNotApplicableForReplacement(vehicle) &&
            !vehicle.areAllNotApplicableForReplacement)
        );
      } else {
        return (
          vehicle.stockType.toUpperCase() === 'SAMPLE' &&
          anyDealNotApplicableForReplacement(vehicle) &&
          !vehicle.areAllNotApplicableForReplacement
        );
      }
    } else {
      return false;
    }
  };

  const isPreownedCaseEnabled = () => {
    return !isLeaseDeal();
  };

  const isLeaseDeal = () => {
    return (
      urlType.toUpperCase() === 'LEASE' ||
      (urlType.toUpperCase() && checkDealTypes(bulkData).hasLeaseDeals)
    );
  };

  const checkReplaceButton = (vehicle) => {
    if (vehicle.isUsed) {
      if (checkDealTypes(bulkData).multipleSelected) {
        return vehicle.isAvailableForReplacementCash || vehicle.isAvailableForReplacementFinance
          ? true
          : false;
      } else {
        return isPreownedCaseEnabled();
      }
    }

    const vehicleType = vehicle.stockType.toUpperCase();

    switch (vehicleType) {
      case 'SAMPLE':
        if (!vehicle.isCurrentOffBrandVehicle) {
          return !vehicle.isOffBrand
            ? isEnabledBasedOnEligibiltyCheck(vehicle, urlType.toLowerCase())
            : false;
        } else {
          return isEnabledBasedOnEligibiltyCheck(vehicle, urlType.toLowerCase());
        }
      case 'NEW':
        return false;
    }
  };

  const submitChangesModal = (vehicle) => {
    const data = {
      vehicle: vehicle,
      bulkData: bulkData,
      isWarning: checkWarningButton(vehicle),
      isAllDealType: urlType.toUpperCase() === 'ALL' ? true : false,
    };
    setSubmitChangeModalData(data);
    setIsSubmitChangeModalOpen(true);
  };

  type RowProps = {
    index: number;
    style: any;
  };

  const Row = React.memo(function Row({ index, style }: RowProps) {
    const vehicle =
      bulkData?.type === 'bulk-inventory'
        ? updateVehicleData(vehicles[index], bulkData.selectedDealer[0])
        : vehicles[index];
    const size = `${vehicle.year} ${vehicle.brandName} ${vehicle.trim}`.replace(/\s/g, '').length;
    return (
      <VehicleInfoRow key={index} style={style} data-testid='vehicle-info-row'>
        <VehicleInfoOne size={size > 38 ? 80 : 68}>
          <VehicleImage key={index}>
            <VehicleInventoryImage
              src={vehicle.imageUrl}
              alt='Vehicle Image'
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                (e.target as HTMLImageElement).onerror = null;
                (e.target as HTMLImageElement).src =
                  'https://static.findmyupgrade.com/miscimages/car-image-holder-2.png';
              }}
            />
          </VehicleImage>
          <InformationOne>
            <VehicleInfoUl>
              <VehicleInfoLiOne>
                <ModelName>
                  {vehicle.year} {vehicle.brandName} {vehicle.trim}
                </ModelName>
                <div style={{ display: 'flex', alignItems: 'baseline' }}>
                  <VehicleTypeBadge
                    style={{ marginLeft: '0px' }}
                    className={`badge condition ${getVehicleTypeBadge(vehicle?.vehicleType)}`}
                  >
                    {vehicle?.vehicleType !== null ? vehicle?.vehicleType : 'Sample'}
                  </VehicleTypeBadge>
                  <div>
                    {isLeaseEnabled(vehicle) && <span className={`deal-type-icon lease`}>L</span>}
                    {isFinanceEnabled(vehicle) && (
                      <span className={`deal-type-icon finance`}>F</span>
                    )}
                    {isCashEnabled(vehicle) && <span className={`deal-type-icon cash`}>C</span>}
                  </div>
                </div>
              </VehicleInfoLiOne>
              <VehicleInfoLiTwo>
                <span className='msrp'>
                  {vehicle.vehicleType === null && 'AVG. MSRP :'}
                  {vehicle?.vehicleType?.toLowerCase() === 'new' && 'MSRP :'}
                  {(vehicle?.vehicleType?.toLowerCase() === 'pre-owned' ||
                    vehicle?.vehicleType?.toLowerCase() === 'certified pre-owned') &&
                    'SELLING PRICE :'}
                </span>
                <span style={{ paddingLeft: '4px' }} className='price'>
                  ${Math.round(vehicle?.listPrice)?.toLocaleString()}
                </span>
              </VehicleInfoLiTwo>
            </VehicleInfoUl>
          </InformationOne>
        </VehicleInfoOne>
        <VehicleInfoTwo>
          <InformationTwo>
            <ul>
              <li className='stock'>
                <div className='val-block'>
                  <span className='list-title'>Stock NUM.</span>
                  <span className='list-item'>{vehicle.stockNumber || 'N/A'}</span>
                </div>
                <div className='val-block'>
                  <span className='list-title'>MILES</span>
                  <span className='list-item'>{vehicle?.miles?.toLocaleString() || '0'}</span>
                </div>
              </li>
              <li className='vin'>
                <div className='val-block'>
                  <span className='list-title'>VIN</span>
                  <span className='list-item'>{vehicle.vin || 'N/A'}</span>
                </div>
                <div className='val-block'>
                  <span className='list-title'>HORSEPOWER</span>
                  <span className='list-item'>{vehicle.horsePower || 'N/A'}</span>
                </div>
              </li>
              <li className='ageDays'>
                <div className='val-block'>
                  <span className='list-title'>AGE DAYS</span>
                  <span className='list-item'>{vehicle.ageDays || 'N/A'}</span>
                </div>
              </li>
            </ul>
          </InformationTwo>
        </VehicleInfoTwo>
        <VehicleInfoThree>
          <InformationThree>
            <ul>
              <li className='ext-color' style={{ paddingRight: '8px' }}>
                <div className='val-block'>
                  <span className='list-title'>EXTERIOR COLOR</span>
                  <span className='list-item'>{vehicle.colorExt}</span>
                </div>
              </li>
              <li className='int-color'>
                <div className='val-block'>
                  <span className='list-title'>INTERIOR COLOR</span>
                  <span className='list-item'>{vehicle.colorInt}</span>
                </div>
              </li>
              {bulkData && bulkData.type === 'bulk-inventory' ? (
                <li className='select-button'>
                  {checkWarningButton(vehicle) && (
                    <span className={'warning-sign'} data-testid='warning-sign'>
                      <Icon
                        type='warning'
                        additionalClassName='am-icon'
                        size={theme.typography.h3}
                        data-testid='warning-icon'
                      />
                    </span>
                  )}
                  {checkReplaceButton(vehicle) ? (
                    <button
                      className={`btn btn-small btn-primary`}
                      data-testid='replace-button'
                      onClick={() => submitChangesModal(vehicle)}
                      disabled={false}
                    >
                      REPLACE (&nbsp; {dealCount} &nbsp;)
                    </button>
                  ) : (
                    <button
                      className={`btn-small btn-primary disabled buttonWithHoverOver`}
                      data-testid='replace-button-disable'
                      onClick={() => submitChangesModal(vehicle)}
                      disabled={true}
                    >
                      REPLACE (&nbsp; {dealCount} &nbsp;)
                    </button>
                  )}
                </li>
              ) : (
                <li className='select-button'>
                  <button
                    className={`btn-small btn-primary buttonWithHoverOver ${
                      permissions?.includes('recalculateDeal') &&
                      !isLeaseEnabled(vehicle) &&
                      !isFinanceEnabled(vehicle) &&
                      !isCashEnabled(vehicle)
                        ? 'disabled'
                        : ''
                    }`}
                    data-testid='select-button'
                    onClick={() => selectVehicle(vehicle)}
                    disabled={
                      !permissions?.includes('recalculateDeal') ||
                      (!isLeaseEnabled(vehicle) &&
                        !isFinanceEnabled(vehicle) &&
                        !isCashEnabled(vehicle))
                    }
                  >
                    Select
                  </button>
                </li>
              )}
            </ul>
          </InformationThree>
        </VehicleInfoThree>
      </VehicleInfoRow>
    );
  });

  const closeModal = () => {
    setIsSubmitChangeModalOpen(false);
  };

  const submitChange = () => {
    SetLoadingPage(true);
    closeModal();
    const { tempData, payload, remainingTotal } = submitChangesCalculation(submitChangeModalData);

    bulkChangeReplacement(
      payload,
      submitChangeModalData.bulkData.selectedDealer[0],
      submitChangeModalData.bulkData.isAllRowsSelected,
    )
      .then((res) => {
        SetLoadingPage(false);
        if (tempData.remaining.length > 0) {
          const remainingData = {
            done: submitChangeModalData.bulkData.isAllRowsSelected
              ? dealCount - remainingTotal
              : tempData.done.length,
            total: dealCount,
            remaining: submitChangeModalData.bulkData.isAllRowsSelected
              ? remainingTotal
              : tempData.remaining.length,
            isBulkSelect: submitChangeModalData.bulkData.isAllRowsSelected,
            closedDealIds: tempData.remaining,
            remainingLeaseDeals: 0,
            remainingCashDeals: 0,
            remainingFinanceDeals: 0,
          };
          dispatch(SetInventoryRemainingModalData(remainingData));
          dispatch(SetInventoryRemainingModal(true));
          dispatch(resetFilters());
          dispatch(InventoryRemove());
          dispatch(setIsInventoryBrowserModalOpen(false));
        } else {
          dispatch(resetFilters());
          dispatch(InventoryRemove());
          dispatch(setIsInventoryBrowserModalOpen(false));
          dispatch(setPortfolioShouldRefresh(true, urlType));
        }
      })
      .catch((err) => {
        SetLoadingPage(false);
        ErrorToast('Failed to update replacement vehicle..', 5000);
      });
  };

  return (
    <InventoryBrowserVehicleList data-testid='inventory-browser-vehicle-list'>
      {loadingPage && <Loader data-testid='loader' coverModal={false} />}
      {!loadingPage &&
        bulkData?.type === 'bulk-inventory' &&
        (featureToggle.privateIncentive || featureToggle.preowned || preownedStatus) && (
          <WarningMsgWrapper data-testid='inventory-browser-warning-msg'>
            {featureToggle.preowned && preownedStatus && (
              <div>
                New replacement vehicle will be saved for the selected deals and will stay
                persistent until changed again. The Predictive Marketing Campaign will automatically
                offer the updated vehicle.
              </div>
            )}

            {featureToggle.privateIncentive && (
              <div>
                *Vehicle replacements will not take effect for customers with an active Private
                Incentive until the incentive has expired.
              </div>
            )}
          </WarningMsgWrapper>
        )}
      {!loadingPage && vehicles.length > 0 && (
        <VariableSizeList
          height={
            (featureToggle.privateIncentive || featureToggle.preowned || preownedStatus) &&
            bulkData?.type === 'bulk-inventory'
              ? 500
              : 600
          } // Height of the list
          itemCount={vehicles.length}
          itemSize={getSize}
          width='100%'
        >
          {Row}
        </VariableSizeList>
      )}
      {!loadingPage && vehicles.length === 0 && (
        <div>
          <Typography
            size={theme.typography.fontSize30}
            fontWeight={theme.typography.fontWeights.normal}
            color={theme.palette.basicGray}
            style={{ position: 'absolute', top: '40%', left: '23%' }}
          >
            No results for your filtering criteria.
          </Typography>
          <ResetButton onClick={() => onResetFiltersClick()}>Reset Filters</ResetButton>
        </div>
      )}
      {isSubmitChangeModalOpen && (
        <ReplaceChangeModal
          isOpen={isSubmitChangeModalOpen}
          submitChange={submitChange}
          closeModal={() => closeModal()}
          data={submitChangeModalData}
        />
      )}
    </InventoryBrowserVehicleList>
  );
};

const ResetButton = styled.button`
  position: absolute;
  top: 50%;
  left: 45%;
  color: rgb(51, 122, 183);
  font-weight: 600;
  text-decoration: none;
  border: none;
  background: none;
  cursor: pointer;
`;

const VehicleImage = styled.div`
  height: 120px;
  width: 30%;
  background: #fff;
  border: 1px solid #dbd5e2;
  margin: 20px 0 0 8px;
  position: absolute;
  top: 0;
  left: 0;
`;

const VehicleInfoRow = styled.div`
  position: relative;
  overflow: hidden;
  height: 188px;
`;

const InventoryBrowserVehicleList = styled.div`
  position: absolute;
  top: 0;
  left: 200px;
  right: 0;
  bottom: 0;
  overflow-y: hidden;
`;

type VehicleInforProps = {
  size: number;
};

const VehicleInfoOne = styled.div<VehicleInforProps>`
  height: ${(props) => props.size}px;
  background-color: #fff;
  display: -ms-flexbox;
  display: flex;
  flex-direction: row-reverse;
  border-bottom: 1px solid rgba(229, 224, 235, 0.5);
`;

const InformationOne = styled.div`
  height: 100%;
  width: 70%;
  align-self: center;
`;

const VehicleInfoTwo = styled.div`
  min-height: 58px;
  background-color: #fff;
  display: flex;
  flex-direction: row-reverse;
`;

const InformationTwo = styled.div`
  height: 100%;
  width: 67%;
  align-self: center;

  > ul {
    list-style: none;
    padding: 0;
    display: table;
    height: 100%;
    width: 100%;
    margin: 0;

    .stock {
      width: 33%;
      vertical-align: top;
      display: table-cell;
    }
    .ageDays {
      display: table-cell;
      vertical-align: top;
      width: 23%;
      padding-right: 18px;
      text-align: right;
    }
    .vin {
      display: table-cell;
      vertical-align: top;
    }
  }

  .val-block {
    line-height: 1;
    margin-bottom: 4px;
    display: block;

    .list-title {
      text-transform: uppercase;
      color: #807f8f;
      font-size: 11px;
      font-weight: 400;
      white-space: nowrap;
      vertical-align: middle;
    }

    .list-item {
      color: #2a2a2a;
      font-size: 11px;
      font-weight: 600;
      margin-left: 5px;
      text-transform: uppercase;
      vertical-align: middle;
    }
  }
`;

const VehicleInfoThree = styled.div`
  background-color: #f9f6fc;
  height: 50px;
  border-bottom: 1px solid #c1a9de;
  min-height: 58px;
  display: flex;
  flex-direction: row-reverse;
`;

const InformationThree = styled.div`
  height: 100%;
  width: 67%;
  align-self: center;

  > ul {
    list-style: none;
    padding: 0;
    display: table;
    height: 100%;
    width: 100%;
    margin: 0;

    .ext-color {
      display: table-cell;
      vertical-align: middle;
      width: 33%;
    }

    .int-color {
      display: table-cell;
      vertical-align: middle;
    }

    .select-button {
      display: table-cell;
      padding-right: 18px;
      text-align: right;
      vertical-align: middle;
      width: 33%;
      }
    .buttonWithHoverOver {
        min-width: 90px;
        position: relative;
        border-radius: 4px;
    }
      .disabled.buttonWithHoverOver:hover:after   {
          content: 'Not eligible for replacement';
          text-transform: none;
          position: absolute;
          bottom: 120%;
          left: 45%;
          transform: translateX(-70%);
          background-color: #4a0e4e;
          color: #fff;
          padding: 5px 10px;
          border-radius: 4px;
          white-space: nowrap;
          font-size: 12px;
          z-index: 1000;
        }
      }
    }

    .warning-sign {
      position: relative;
      padding: 2px 10px;
      display: ruby;
      :hover::after {
        content: 'Not all deal types can be mapped to this vehicle';
        text-transform: none;
        position: absolute;
        bottom: 120%;
        left: 45%;
        transform: translateX(-70%);
        background-color: #4a0e4e;
        color: #fff;
        padding: 5px 10px;
        border-radius: 4px;
        white-space: nowrap;
        font-size: 12px;
        z-index: 1000;
      }
    }

    .val-block {
      line-height: 1;
      margin-bottom: 4px;
      display: block;

      .list-title {
        text-transform: uppercase;
        color: #593487;
        font-size: 11px;
        font-weight: 400;
        white-space: nowrap;
        vertical-align: middle;
      }

      .list-item {
        color: #2a2a2a;
        font-size: 11px;
        font-weight: 600;
        margin-left: 5px;
        text-transform: uppercase;
        vertical-align: middle;
      }
    }
  }
`;

const VehicleInfoUl = styled.ul`
  list-style: none;
  padding: 0;
  display: table;
  height: 100%;
  width: 100%;
  margin: 0;
`;

const VehicleInfoLiOne = styled.li`
  width: 70%;
  padding-top: 10px;
  padding-left: 24px;
  vertical-align: middle;
  display: table-cell;
  list-style: none;

  .deal-type-icon {
    padding: 1px 4px 1px 4px;
    color: white;
    background: ${theme.palette.blue};
    border-radius: 2px;
    font-size: 11px;
    font-weight: ${theme.typography.fontWeights.bold};
    margin-left: 5px;
    margin-right: 0px;
    margin-top: 0px;
    &.cash {
      background-color: ${theme.palette.green};
    }
    &.finance {
      background-color: ${theme.palette.yellow};
    }
  }
`;

const VehicleInfoLiTwo = styled.li`
  width: 30%;
  vertical-align: middle;
  display: table-cell;
  text-align: end;
  padding-right: 15px;

  .msrp {
    text-transform: uppercase;
    color: #807f8f;
    font-size: 11px;
    font-weight: 400;
    white-space: nowrap;
    vertical-align: initial;
  }

  .price {
    color: #2a2a2a;
    font-size: 16px;
    font-weight: 400;
    vertical-align: middle;
  }
`;

const ModelName = styled.span`
  color: #653e97;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 3px;
  display: block;
  box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
  vertical-align: middle;
`;

const VehicleTypeBadge = styled.div`
  margin: 5px;
  padding: 5px 10px 5px 10px;
  border-radius: 0.25rem !important;
`;

const WarningMsgWrapper = styled.div`
  margin: 8px 8px 0;
  border: 1px solid #c6beae;
  border-radius: 4px;
  padding: 7px 15px;
  margin-bottom: 10px;
  background-color: #fff9dc;
  color: #8a6d3b;
`;

export default VehicleList;
